// Section 14 departures (stream E2). People who leave take 1 food each; the report names them
// and their reason; households close to them (their partners', parents' and children's) lose
// trust -2; their posts fall vacant; they are kept in `formerPeople` with fate `left` (or
// `exiled`). Exile (Section 13) is a forced departure: the partner follows half the time and
// children go with a leaving parent unless the other parent stays.
//
// Shared (M6): the migration phase uses every export; the steady phase calls `departHousehold`
// for the Section 20 breakdown ("households leave en masse").
import { ageGroupOf, peopleSorted } from '../../core/queries';
import { clamp } from '../../core/math';
import type {
  EmigrationReason,
  HouseholdId,
  Person,
  PersonId,
  PhaseContext,
} from '../../core/types';
import { addStock } from './stocks';
import { num } from './value';

/** Remove one person from the settlement, keeping links for the family graph and the Ledger. */
function remove(
  ctx: PhaseContext,
  p: Person,
  reason: EmigrationReason,
  leaving: ReadonlySet<PersonId>,
): void {
  const d = ctx.draft;
  if (p.job !== 'rest')
    ctx.report.add({ kind: 'job-vacant', jobId: p.job, personId: p.id, reason: 'left' });
  const h = d.households[p.householdId];
  if (h !== undefined) {
    h.memberIds = h.memberIds.filter((id) => id !== p.id);
    if (h.memberIds.length === 0) delete d.households[h.id];
  }
  if (p.partnerId !== null && !leaving.has(p.partnerId)) {
    const partner = d.people[p.partnerId];
    if (partner !== undefined) partner.partnerId = null;
  }
  for (const q of peopleSorted(d)) if (q.masterId === p.id) q.masterId = null;
  delete d.plantedPlots[p.id];
  delete d.toolOverrides[p.id];
  delete d.triageOverrides[p.id];
  for (const overrides of Object.values(d.rationOverrides)) delete overrides.people[p.id];
  d.myHousehold = d.myHousehold.filter((id) => id !== p.id);
  d.formerPeople[p.id] = {
    id: p.id,
    name: p.name,
    sex: p.sex,
    ageSeasons: p.ageSeasons,
    ageGroup: ageGroupOf(ctx.cfg, p.ageSeasons),
    origin: p.origin,
    job: p.job,
    householdId: p.householdId,
    partnerId: p.partnerId,
    parentIds: p.parentIds,
    childIds: p.childIds,
    turn: ctx.season.turn,
    fate:
      reason === 'exile' && p.plannedDeparture?.reason === 'exile'
        ? { kind: 'exiled' }
        : { kind: 'left', reason },
  };
  delete d.people[p.id];
}

/** Section 14: trust -2 for the households of kin who stay behind (once per household). */
function grieveDeparture(ctx: PhaseContext, leavers: readonly Person[]): void {
  const d = ctx.draft;
  const leaving = new Set(leavers.map((p) => p.id));
  const households = new Set<HouseholdId>();
  for (const p of leavers) {
    for (const kin of [p.partnerId, ...p.parentIds, ...p.childIds]) {
      if (kin === null || leaving.has(kin)) continue;
      const k = d.people[kin];
      if (k !== undefined) households.add(k.householdId);
    }
  }
  let people = 0;
  let total = 0;
  for (const hid of [...households].sort()) {
    for (const id of d.households[hid]?.memberIds ?? []) {
      const q = d.people[id];
      if (q === undefined) continue;
      const change = num(ctx.live, { kind: 'trustChange', personId: id, cause: 'departure' });
      q.trust = clamp(q.trust + change, 0, ctx.cfg.people.statMax);
      people++;
      total += change;
    }
  }
  if (people > 0)
    ctx.report.add({ kind: 'trust', cause: 'departure', delta: total / people, people });
}

/**
 * People leave together (one departure entry per household they leave from). Each takes
 * `foodPerLeaver` food. Returns the ids that left.
 */
export function departPeople(
  ctx: PhaseContext,
  ids: readonly PersonId[],
  reason: EmigrationReason,
): PersonId[] {
  const d = ctx.draft;
  const leavers = [...new Set(ids)]
    .sort()
    .map((id) => d.people[id])
    .filter((p): p is Person => p !== undefined);
  if (leavers.length === 0) return [];
  const leaving = new Set(leavers.map((p) => p.id));
  const byHousehold = new Map<HouseholdId, PersonId[]>();
  for (const p of leavers)
    byHousehold.set(p.householdId, [...(byHousehold.get(p.householdId) ?? []), p.id]);
  const snapshot = leavers.map((p) => JSON.parse(JSON.stringify(p)) as Person);
  addStock(ctx, 'food', -leavers.length * ctx.cfg.migration.foodPerLeaver, 'other');
  for (const p of leavers) remove(ctx, p, reason, leaving);
  for (const [householdId, personIds] of [...byHousehold].sort((a, b) => (a[0] < b[0] ? -1 : 1)))
    ctx.report.add({ kind: 'departure', personIds, householdId, reason });
  ctx.report.current.population.departures += leavers.length;
  grieveDeparture(ctx, snapshot);
  return leavers.map((p) => p.id);
}

/** Section 14 and 20: a whole household leaves. Returns the ids that left. */
export function departHousehold(
  ctx: PhaseContext,
  householdId: HouseholdId,
  reason: EmigrationReason,
): PersonId[] {
  const h = ctx.draft.households[householdId];
  return h === undefined ? [] : departPeople(ctx, h.memberIds, reason);
}

/**
 * Section 13 and 14 exile: the exiled person leaves; their partner follows half the time (main
 * stream: the draw depends on who was exiled); their children under 15 go with them unless the
 * other parent stays.
 */
export function exile(ctx: PhaseContext, personId: PersonId): PersonId[] {
  const d = ctx.draft;
  const p = d.people[personId];
  if (p === undefined) return [];
  const going = new Set<PersonId>([p.id]);
  const partner = p.partnerId === null ? undefined : d.people[p.partnerId];
  if (
    partner !== undefined &&
    ctx.rng.happened(ctx.rng.chance(ctx.cfg.justice.exile.partnerFollows))
  )
    going.add(partner.id);
  for (const childId of [...p.childIds].sort()) {
    const child = d.people[childId];
    if (child === undefined || child.ageSeasons >= ctx.cfg.migration.dependantUnderSeasons)
      continue;
    const otherStays = child.parentIds.some(
      (id) => id !== p.id && d.people[id] !== undefined && !going.has(id),
    );
    if (!otherStays) going.add(child.id);
  }
  return departPeople(ctx, [...going], 'exile');
}
